/**
 * Client for /api/excerpts (#277). Types mirror server/src/lib/excerpts/service.ts.
 */
import type { JSONContent } from '@tiptap/core';
import { apiDelete, apiDeleteJson, apiGet, apiPost } from './api';

export type ExcerptKind = 'excerpt' | 'task' | 'note';
export type SourceType = 'meeting_instance' | 'task' | 'note' | 'scratchpad';
export type DestType = SourceType | 'scope' | 'meeting_series' | 'tag' | 'reference_doc' | 'person';

export interface ItemRef { type: DestType; id: string }
export interface Titled { type: string; id: string; title: string }

export interface ExcerptView {
  id: string;
  kind: ExcerptKind;
  status: string;
  live: boolean;
  deleted: boolean;
  fragment: JSONContent | null;
  plain_text: string | null;
  source: Titled;
  linked_entity_id: string | null;
  destinations: Titled[];
  created_at: string;
  updated_at: string;
  via?: Titled;
  /** Series lists only (#290): made in one of the series' meetings. */
  made_here?: boolean;
}

export const SOURCE_TYPES: readonly SourceType[] = ['meeting_instance', 'task', 'note', 'scratchpad'];
export const isSourceType = (t: string): t is SourceType => (SOURCE_TYPES as readonly string[]).includes(t);

/** The body field each source type autosaves, and its detail route. */
export const SOURCE_ROUTES: Record<SourceType, { path: string; field: string }> = {
  meeting_instance: { path: '/meeting-instances', field: 'note_content' },
  task: { path: '/tasks', field: 'description' },
  note: { path: '/notes', field: 'content' },
  scratchpad: { path: '/scratchpads', field: 'content' },
};

export interface CreateExcerptInput {
  source_type: SourceType;
  source_id: string;
  kind: ExcerptKind;
  destinations?: ItemRef[];
  /** The selected text: the history label, and the title for a task/note. */
  text?: string;
  title?: string;
  /** Body destinations whose open editor inserts the embed itself. */
  placed?: ItemRef[];
}

export const createExcerpt = (input: CreateExcerptInput) =>
  apiPost<{ id: string; linked_entity_id: string | null }>('/excerpts', input);

export const getExcerpts = (ids: string[]) =>
  apiGet<ExcerptView[]>(`/excerpts?ids=${ids.map(encodeURIComponent).join(',')}`);

export const listBySource = (type: SourceType, id: string) =>
  apiGet<ExcerptView[]>(`/excerpts/by-source/${type}/${encodeURIComponent(id)}`);

export const listByDestination = (type: DestType, id: string, rollup = false) =>
  apiGet<ExcerptView[]>(`/excerpts/by-destination/${type}/${encodeURIComponent(id)}${rollup ? '?rollup=true' : ''}`);

export const addDestination = (id: string, dest: ItemRef, placed = false) =>
  apiPost<ExcerptView>(`/excerpts/${encodeURIComponent(id)}/destinations`, { ...dest, placed });

export const removeDestination = (id: string, dest: ItemRef) =>
  apiDeleteJson<{ excerptNowEmpty: boolean }>(`/excerpts/${encodeURIComponent(id)}/destinations/${dest.type}/${encodeURIComponent(dest.id)}`);

export const deleteExcerpt = (id: string, deleteLinked = false) =>
  apiDelete(`/excerpts/${encodeURIComponent(id)}${deleteLinked ? '?deleteLinked=true' : ''}`);

export const deletePreflight = (type: SourceType, id: string) =>
  apiGet<{ excerptCount: number; destinationItemCount: number }>(`/excerpts/delete-preflight/${type}/${encodeURIComponent(id)}`);

/** "3 excerpts appear in 2 other items." for a delete confirmation, or '' when none. */
export async function excerptDeleteWarning(type: SourceType, id: string): Promise<string> {
  try {
    const { excerptCount: e, destinationItemCount: d } = await deletePreflight(type, id);
    if (!e) return '';
    return `${e} excerpt${e === 1 ? '' : 's'} from it appear${e === 1 ? 's' : ''} in ${d} other item${d === 1 ? '' : 's'} and will be removed from ${d === 1 ? 'it' : 'them'}.`;
  } catch {
    return '';
  }
}
